import { isDefined } from '@sgme/fp';
import type { LiveSummaryMailRequest } from 'components/common-form/sidePanel/docToolbar/summary/models/liveSummaryMailRequest';
import { useTradeWayLabel } from 'components/secondaryMarket/shared/status/tradeWay';
import { useSPIntl } from 'hooks/useIntl';
import { useSelector } from 'react-redux';
import { liveFormInputSelectors } from 'state/secondaryMarket/live/form/input/inputForm.selectors';
import { liveSelectors } from 'state/secondaryMarket/live/live.selector';
import {
  getLiveInitialUpfront,
  getLiveQuotePriceType,
  getLiveQuoteQuoteId,
  getLiveQuoteSettlementDate,
  getLiveQuoteTradeWay,
  getLiveQuoteUpfront,
} from 'state/secondaryMarket/live/quote/liveQuote.selector';
import { liveReferentialSelectors } from 'state/secondaryMarket/live/referential/liveReferential.selector';
import { liveTradeSelectors } from 'state/secondaryMarket/live/trade/trade.selector';
import type { AppState } from 'state/state.model';

export const useLiveSummary = (tabId: string) => {
  const { formatDisplayedDate, formatNumber } = useSPIntl();

  const quoteId = useSelector((state: AppState) => getLiveQuoteQuoteId(state, tabId));
  const tradeWay = useSelector((state: AppState) => getLiveQuoteTradeWay(state, tabId));
  const initialUpfront = useSelector((state: AppState) => getLiveInitialUpfront(state, tabId));
  const liveUpfront = useSelector((state: AppState) => getLiveQuoteUpfront(state, tabId));
  const quantity = useSelector((state: AppState) => liveFormInputSelectors.getFormQuantity(state, tabId));
  const effectiveCashAmount = useSelector((state: AppState) => liveTradeSelectors.getCashAmount(state, tabId));
  const tradeDate = useSelector((state: AppState) => liveTradeSelectors.getTradeTradeDate(state, tabId));
  const settlementDate = useSelector((state: AppState) => getLiveQuoteSettlementDate(state, tabId));
  const priceCurrency = useSelector((state: AppState) => liveReferentialSelectors.getCurrency(state, tabId));
  const priceType = useSelector(
    (state: AppState) => getLiveQuotePriceType(state, tabId) ?? liveReferentialSelectors.getPriceType(state, tabId)!,
  );
  const isinCode = useSelector((state: AppState) => liveReferentialSelectors.getIsin(state, tabId));
  const ric = useSelector((state: AppState) => liveReferentialSelectors.getRic(state, tabId));
  const fileCode = useSelector((state: AppState) => liveReferentialSelectors.getFileCode(state, tabId));
  const client = useSelector((state: AppState) => liveSelectors.getFormClient(state, tabId));
  const clientType = useSelector((state: AppState) => liveSelectors.getFormClientType(state, tabId));
  const custodian = useSelector((state: AppState) => liveSelectors.getFormCustodian(state, tabId));
  const liveSettlementUnit = useSelector((state: AppState) =>
    liveFormInputSelectors.getFormTradeSizeInputMode(state, tabId),
  );
  const settlementPrice = useSelector((state: AppState) =>
    liveSelectors.getSalesAdjustedSettlementPriceFormatted(state, tabId),
  );
  const traceReporting = useSelector((state: AppState) =>
    liveFormInputSelectors.getFormInputTraceReporting(state, tabId),
  );

  const eligibleTraceReporting = useSelector((state: AppState) =>
    liveReferentialSelectors.getEligibleTraceReporting(state, tabId),
  );
  const tradeWayLabel = useTradeWayLabel({ way: tradeWay });

  const upfront = liveUpfront !== null ? liveUpfront : initialUpfront;

  const liveSummaryMailRequestProps: LiveSummaryMailRequest = {
    quoteId: quoteId,
    tradeWay: tradeWayLabel,
    upfront:
      priceType === 'Percent'
        ? `${formatNumber(upfront! * 100.0, { maximumFractionDigits: 4 })} %`
        : `${formatNumber(upfront!, { maximumFractionDigits: 4 })} ${priceCurrency}`,
    quantity: quantity,
    settlementCashAmount:
      effectiveCashAmount !== undefined
        ? `${formatNumber(effectiveCashAmount!, { maximumFractionDigits: 4 })} ${priceCurrency}`
        : ' ',
    settlementPrice:
      settlementPrice !== undefined
        ? priceType === 'Percent'
          ? `${formatNumber(settlementPrice, { maximumFractionDigits: 4 })} %`
          : `${formatNumber(settlementPrice, { maximumFractionDigits: 4 })} ${priceCurrency}`
        : ' ',
    tradeDate: formatDisplayedDate(tradeDate),
    settlementDate: formatDisplayedDate(settlementDate),
    priceCurrency: priceCurrency,
    priceType: priceType,
    isinCode: isinCode,
    ric: ric,
    fileCode: fileCode,
    client: clientType !== 'Other' ? clientType : `${client?.label} (${client?.id} - ${client?.eliotCode})`,
    custodian: `${custodian?.label} (${custodian?.id} - ${custodian?.eliotCode})`,
    liveSettlementUnit: isDefined(liveSettlementUnit)
      ? liveSettlementUnit.charAt(0).toUpperCase() + liveSettlementUnit.slice(1)
      : liveSettlementUnit,
    traceReporting: eligibleTraceReporting === false ? undefined : traceReporting === true ? 'Yes' : 'No',
  };
  return liveSummaryMailRequestProps;
};
